/**
 * nativePush.js — 安卓 APK「前台服务长连接推送」的 JS 侧封装。
 * 浏览器 / PWA 环境（非 Capacitor 原生）下全部是空操作，不影响任何现有功能。
 * 原生插件名：ForegroundPush（见 android 的 ForegroundPushPlugin.java）。
 * 旧版 APK 没有该插件时，调用会被 try/catch 吞掉，同样无影响。
 */
import api from './api';

const LS_TOKEN = 'native_push_device_token';
const LS_BATT  = 'native_push_battery_asked';
let _plugin;   // undefined = 未探测；null = 不可用（非原生/无插件）

async function getPlugin() {
  if (_plugin !== undefined) return _plugin;
  try {
    const { Capacitor, registerPlugin } = await import('@capacitor/core');
    _plugin = Capacitor.isNativePlatform() ? registerPlugin('ForegroundPush') : null;
  } catch {
    _plugin = null;
  }
  return _plugin;
}

/** 登录后调用：申请通知权限 → 换设备令牌 → 启动原生前台服务 */
export async function startNativePush(user) {
  const plugin = await getPlugin();
  if (!plugin || !user) return;
  try {
    // Android 13+ 需要通知权限：由原生插件直接申请（不依赖 @capacitor/push-notifications / Firebase）
    try { await plugin.requestNotificationPermission(); } catch { /* 被拒绝也继续：服务照常运行，只是通知不显示 */ }

    const res = await api.post('/device-push/token', {
      deviceToken: localStorage.getItem(LS_TOKEN) || undefined,
    });
    const deviceToken = res.data && res.data.deviceToken;
    if (!deviceToken) return;
    localStorage.setItem(LS_TOKEN, deviceToken);

    await plugin.start({
      serverUrl: window.location.origin,
      deviceToken,
      userId: String(user.id),
    });

    // 首次：弹系统的「允许后台运行（忽略电池优化）」对话框，只问一次
    if (!localStorage.getItem(LS_BATT)) {
      localStorage.setItem(LS_BATT, '1');
      setTimeout(() => { plugin.requestIgnoreBatteryOptimizations().catch(() => {}); }, 1500);
    }
  } catch (e) {
    console.warn('[native-push]', e && e.message);
  }
}

/** 退出登录时调用：停止前台服务并注销设备令牌（令牌自身鉴权，JWT 过期也能成功） */
export async function stopNativePush() {
  const plugin = await getPlugin();
  if (!plugin) return;
  try {
    const dt = localStorage.getItem(LS_TOKEN);
    await plugin.stop();
    if (dt) {
      // 不 await：断网时不能拖住退出登录。原生端已清除令牌，服务器上的旧令牌 60 天后自动作废。
      api.delete('/device-push/token', { headers: { 'X-Device-Token': dt }, timeout: 5000 }).catch(() => {});
      localStorage.removeItem(LS_TOKEN);
    }
  } catch { /* 静默 */ }
}

/** 可选：给「我的」页放个按钮，引导用户去系统设置里放开后台/自启动 */
export async function openBackgroundSettings() {
  const plugin = await getPlugin();
  if (!plugin) return false;
  try { await plugin.openAppSettings(); return true; } catch { return false; }
}
